/** Who is carrying what.
 *
 * Busiest first, because the question this answers is "who is overloaded" — sorting
 * alphabetically would bury it. Counts live projects and staffed roles only: including
 * finished work and people who were merely consulted reports everyone as busy, which is
 * the same as reporting nothing.
 */

import { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'

import { EmptyState, ErrorState, Loading, Pill, StatCard } from '@/components/ui'
import { ragTone, statusLabel } from '@/lib/domain'
import { useWorkload } from '@/features/delivery/useDelivery'

export function WorkloadPage() {
  const { data, isPending, error } = useWorkload()
  /** Which view the page is showing. `assignments` is a flat list, the rest group by person. */
  const [lens, setLens] = useState<'all' | 'atRisk' | 'busiest' | 'assignments'>('all')

  /**
   * Both memos sit above the early returns, because a hook after a conditional return runs
   * in a different order between renders. eslint's `rules-of-hooks` caught this the moment
   * it was written, which is the reason it is configured as an error.
   *
   * `people` is memoised for the same reason it is elsewhere: `?? []` builds a new array
   * every render, which would defeat the memo below it.
   */
  const people = useMemo(() => data?.people ?? [], [data])
  const flatAssignments = useMemo(
    () =>
      people.flatMap((person) =>
        person.projects.map((project) => ({
          person,
          project,
          key: `${person.userId}-${project.id}`,
        })),
      ),
    [people],
  )

  if (isPending) return <Loading label="Working out who has what" />
  if (error) return <ErrorState error={error} />

  if (people.length === 0) {
    return (
      <div className="mx-auto max-w-[1000px] pt-2">
        <EmptyState title="Nobody is assigned to a live project">
          Add people to projects from the project drawer and they will appear here.
        </EmptyState>
      </div>
    )
  }

  const busiest = people[0]
  const total = data?.totalAssigned ?? 0

  /**
   * Every number here presses through to the set behind it.
   *
   * "Assignments" needed a genuinely different view rather than a filter: the number is a
   * count of person-project pairs, so what sits behind it is a flat list of all of them,
   * not a subset of people. "Most loaded" narrows to the one person — a set of one is
   * still the honest answer to "who is that".
   */
  const shown =
    lens === 'atRisk'
      ? people.filter((person) => person.atRisk > 0)
      : lens === 'busiest' && busiest
        ? [busiest]
        : people

  return (
    <div className="mx-auto max-w-[1000px] pt-2">
      <header className="mb-6">
        <h1 className="font-heading text-3xl text-fg">Workload</h1>
        <p className="mt-1 text-sm text-fg-muted">
          Live projects only, and only people actually staffed on them. Stakeholders are
          consulted rather than assigned, so they are not counted here.
        </p>
        <p className="mt-1 text-sm text-fg-muted">
          Lead and contributor are the role each person holds on the project&apos;s Team list,
          set in the project drawer. Press a project to open it and see what it is and who
          else is on it.
        </p>
      </header>

      <div className="mb-2 flex flex-wrap gap-3">
        <StatCard
          label="People assigned"
          value={people.length}
          tone="info"
          hint="Show everyone"
          active={lens === 'all'}
          onClick={() => setLens('all')}
        />
        <StatCard
          label="Assignments"
          value={total}
          tone="neutral"
          hint="List every person-project pairing"
          active={lens === 'assignments'}
          onClick={() => setLens(lens === 'assignments' ? 'all' : 'assignments')}
        />
        <StatCard
          label="Most loaded"
          value={busiest ? busiest.leading + busiest.contributing : 0}
          tone="amber"
          hint={busiest ? `Show ${busiest.displayName} only` : undefined}
          active={lens === 'busiest'}
          onClick={() => setLens(lens === 'busiest' ? 'all' : 'busiest')}
        />
        <StatCard
          label="On at-risk work"
          value={people.filter((person) => person.atRisk > 0).length}
          tone="red"
          hint="Show only the people carrying amber or red projects"
          active={lens === 'atRisk'}
          onClick={() => setLens(lens === 'atRisk' ? 'all' : 'atRisk')}
        />
      </div>

      <p className="mb-6 text-xs text-fg-muted">
        Press a number to see what is behind it. Press it again to show everyone.
      </p>

      {lens === 'assignments' ? (
        <ul className="card divide-y divide-border">
          {flatAssignments.map(({ person, project, key }) => (
            <li key={key}>
              <Link
                to={`/projects/${project.id}`}
                className="flex flex-wrap items-center gap-3 px-4 py-2.5 hover:bg-canvas"
              >
                <span
                  className="h-2.5 w-2.5 shrink-0 rounded-full"
                  style={{
                    backgroundColor: `var(--status-${
                      project.ragStatus === 'none' ? 'info' : project.ragStatus
                    })`,
                  }}
                  aria-hidden
                />
                <span className="w-44 shrink-0 truncate text-sm text-fg">{person.displayName}</span>
                <span className="min-w-0 flex-1 truncate text-sm text-fg-muted">{project.name}</span>
                {project.role === 'lead' ? <Pill tone="info">lead</Pill> : <Pill>contributor</Pill>}
                <span className="text-xs text-fg-muted">{statusLabel(project.status)}</span>
              </Link>
            </li>
          ))}
        </ul>
      ) : (
      <ul className="space-y-3">
        {shown.map((person) => {
          const load = person.leading + person.contributing
          const share = busiest ? (load / (busiest.leading + busiest.contributing)) * 100 : 0
          return (
            <li key={person.userId} className="card p-4">
              <div className="flex flex-wrap items-center gap-3">
                <div className="min-w-0 flex-1">
                  <div className="truncate font-medium text-fg">{person.displayName}</div>
                  <div className="truncate text-xs text-fg-muted">{person.email}</div>
                </div>
                {person.leading > 0 ? <Pill tone="info">{person.leading} leading</Pill> : null}
                {person.contributing > 0 ? (
                  <Pill tone="neutral">{person.contributing} contributing</Pill>
                ) : null}
                {person.atRisk > 0 ? <Pill tone="red">{person.atRisk} at risk</Pill> : null}
                <span className="font-heading text-2xl leading-none text-fg">{load}</span>
              </div>

              {/* Relative to the busiest person, which is the only comparison that matters. */}
              <div className="mt-3 h-2 w-full overflow-hidden rounded-pill bg-canvas">
                <div
                  className="h-full rounded-pill"
                  style={{
                    width: `${share}%`,
                    backgroundColor:
                      person.atRisk > 0 ? 'var(--status-amber)' : 'var(--status-info)',
                  }}
                />
              </div>

              <ul className="mt-3 flex flex-wrap gap-2">
                {person.projects.map((project) => (
                  <li key={project.id}>
                    <Link
                      to={`/projects/${project.id}`}
                      className="flex items-center gap-2 rounded-md border border-border px-2 py-1 text-xs hover:bg-canvas"
                      title={`${statusLabel(project.status)} — open the project`}
                    >
                      <span
                        className="inline-block h-2 w-2 rounded-full"
                        style={{
                          backgroundColor: `var(--status-${
                            project.ragStatus === 'none' ? 'info' : project.ragStatus
                          })`,
                        }}
                        aria-hidden
                      />
                      <span className="text-fg">{project.name}</span>
                      {project.role === 'lead' ? (
                        <Pill tone={ragTone(project.ragStatus)}>lead</Pill>
                      ) : null}
                    </Link>
                  </li>
                ))}
              </ul>
            </li>
          )
        })}
      </ul>
      )}
    </div>
  )
}
